import { clsx } from 'clsx'
import { type FC } from 'react'

import {
  type GkAirRecommendedProductDataWeb,
  type GkAirRecommendedProductWeb,
} from '@redteclab/api/clients/crosssellers-proxy-service'

import { Link, Skeleton } from 'base-ui'

import {
  RECOMMENDATION_ANALYTICS_ATTR_IMAGE,
  RECOMMENDATION_ANALYTICS_ATTR_TITLE,
} from '../../../../cross-sell'
import { EXPERIMENTS_TRACK_EVENT_RECOMMENDATION_PRODUCT_CLICK } from '../../../../experiments/eventTracking'
import {
  CSW_INTERACTED_KEY,
  useSetSessionStorageValuesForOptimizelyTracking,
} from '../../../../experiments/hooks'
import { recommendationAttributionFromRecommendedProduct } from '../../../../experiments/recommendation-attribution'
import { ProductPicture } from '../../../../product'
import {
  recommendationExperimentsTrackRecommendationEvent,
  useRecommendationWidgetContext,
} from '../../../../recommendation'
import { ProductRedPointsBadge } from '../../ProductRedPointsBadge'

type ProductRecommendationWidgetProductThumbnailProps = {
  className?: string
  imageAsUrl?: boolean
  imageUrl?: string
  onGtmDataLayerPush?: () => void
  position?: number
  product?: GkAirRecommendedProductWeb
  productId: number | string
  productName: string
  productUrl: string
  recommendedProduct?: GkAirRecommendedProductDataWeb
  redpoints?: number
}

export const ProductRecommendationAreaProductThumbnail: FC<
  ProductRecommendationWidgetProductThumbnailProps
> = ({
  className,
  imageAsUrl,
  imageUrl,
  onGtmDataLayerPush,
  position,
  product,
  productId,
  productName,
  productUrl,
  recommendedProduct,
  redpoints,
}) => {
  const trackClick =
    useSetSessionStorageValuesForOptimizelyTracking(CSW_INTERACTED_KEY)
  const {
    saveUdpProductClickAttribution,
    serviceId,
    trackUdpProductImpression,
    widgetId,
  } = useRecommendationWidgetContext()

  if (!recommendedProduct) {
    return <Skeleton className="h-56 w-40" variant="rectangular" />
  }

  const trackRecommendationProductClick = (): void => {
    if (recommendedProduct.UID) {
      saveUdpProductClickAttribution?.(recommendedProduct.UID)
    }

    recommendationExperimentsTrackRecommendationEvent({
      attribution:
        recommendationAttributionFromRecommendedProduct(recommendedProduct),
      eventKey: EXPERIMENTS_TRACK_EVENT_RECOMMENDATION_PRODUCT_CLICK,
      offerId: recommendedProduct.offer_id,
      serviceId,
      widgetId,
    })
  }

  const productImage = (
    <ProductPicture
      className="h-auto max-w-full"
      imageProps={{
        alt: '',
        'data-analytics': RECOMMENDATION_ANALYTICS_ATTR_IMAGE,
        height: 150,
        src: imageUrl,
        width: 150,
      }}
    />
  )

  return (
    <div
      className={clsx('relative', className)}
      data-product-id={productId}
      ref={
        product && position
          ? trackUdpProductImpression?.({
              position,
              product,
            })
          : undefined
      }
    >
      {imageAsUrl ? (
        <a
          aria-label={productName}
          className="mb-2 flex aspect-square items-center justify-center bg-light-primary-low p-3.5"
          data-analytics={RECOMMENDATION_ANALYTICS_ATTR_TITLE}
          href={productUrl}
          onClick={() => {
            onGtmDataLayerPush?.()
            trackClick()
            trackRecommendationProductClick()
          }}
        >
          {productImage}
        </a>
      ) : (
        <div className="mb-2 flex aspect-square items-center justify-center bg-light-primary-low p-3.5">
          {productImage}
        </div>
      )}
      <h3 className="text-center">
        <Link
          className="line-clamp-3 text-center font-medium no-underline"
          color="inherit"
          data-analytics={RECOMMENDATION_ANALYTICS_ATTR_TITLE}
          href={productUrl}
          onClick={() => {
            onGtmDataLayerPush?.()
            trackClick()
            trackRecommendationProductClick()
          }}
        >
          {productName}
        </Link>
      </h3>
      {redpoints ? (
        <div className="absolute right-0 top-0">
          <ProductRedPointsBadge
            className="size-16 text-xs font-normal lowercase"
            redPointsAmount={redpoints}
          />
        </div>
      ) : null}
    </div>
  )
}
